import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useDashboardData } from "@/hooks/useDashboardData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Sector,
  BarChart,
  Legend,
  Bar,
  Rectangle,
} from "recharts";
import { Empty } from "@/components/Empty";
import CreateAccount from "./account/Create";
import { Skeleton } from "@/components/ui/skeleton";

export default function Dashboard() {
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const data = useDashboardData(
    dateRange.from.toISOString().split("T")[0],
    dateRange.to.toISOString().split("T")[0]
  );

  const renderActiveShape = (props: any) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          {...{ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }}
        />
        <Sector
          {...{ cx, cy, startAngle, endAngle }}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          fill="#333"
          textAnchor={textAnchor}
        >
          {`Movimientos ${value}`}
        </text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          fill="#999"
          textAnchor={textAnchor}
        >
          {`(${(percent * 100).toFixed(1)}%)`}
        </text>
      </g>
    );
  };
  const limpiar = (val: any) => {
    setOpen(val);
  };
  async function fetchAccounts() {
    window.location.reload();
  }
  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  return (
    <div className="p-6 space-y-6 w-full mx-auto">
      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          {Object.entries(data).length === 0 ? (
            <Empty
              title="Bienvenido a Finanza"
              message="Aún no tienes cuentas registradas."
            >
              <CreateAccount
                open={open}
                setOpen={limpiar}
                editAccount={null}
                fetchAccounts={fetchAccounts}
                limpiar={limpiar}
              ></CreateAccount>
            </Empty>
          ) : (
            <>
              <div className="flex md:flex-row md:justify-between md:items-center flex-col">
                <h1 className="text-2xl font-bold">Dashboard</h1>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="p-2 px-5 rounded-lg border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50">
                      {format(dateRange.from, "dd MMM yyyy")} -{" "}
                      {format(dateRange.to, "dd MMM yyyy")}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDateRange({ from: range.from, to: range.to });
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex  gap-4 overflow-auto   flex-nowrap">
                <Card style={{ minWidth: 250 }}>
                  <CardHeader>
                    <CardTitle>
                      <div className="text-sm text-muted-foreground">
                        Ingresos
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-semibold">
                      <>
                        {Object.entries(data).map(([currency, stats]) => (
                          <p>
                            {currency} {stats?.totalIncome.toFixed(2)}
                          </p>
                        ))}
                      </>
                    </p>
                  </CardContent>
                </Card>
                <Card style={{ minWidth: 250 }}>
                  <CardHeader>
                    <CardTitle>
                      <div className="text-sm text-muted-foreground">
                        Egresos
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-semibold">
                      {Object.entries(data).map(([currency, stats]) => (
                        <p>
                          {currency} {stats?.totalExpense.toFixed(2)}
                        </p>
                      ))}
                    </p>
                  </CardContent>
                </Card>
                <Card style={{ minWidth: 250 }}>
                  <CardHeader>
                    <CardTitle>
                      <div className="text-sm text-muted-foreground">
                        Balance Neto
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-semibold">
                      {Object.entries(data).map(([currency, stats]) => (
                        <p>
                          {currency} {stats?.netBalance.toFixed(2)}
                        </p>
                      ))}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Gráfico de flujo */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {Object.entries(data).map(([currency, stats]) => {
                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle>Flujo Diario {currency}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {stats.trends.length === 0 ? (
                          <Empty
                            title="Sin Flujo diario"
                            message="Aún no tienes transacciones."
                          ></Empty>
                        ) : (
                          <>
                            <ResponsiveContainer width="100%" height={300}>
                              <AreaChart
                                width={500}
                                height={400}
                                data={stats.trends}
                                margin={{
                                  top: 10,
                                  right: 30,
                                  left: 0,
                                  bottom: 0,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Area
                                  type="monotone"
                                  dataKey="income"
                                  stroke="#22c55e"
                                  fill="#22c55e"
                                  name="Ingresos"
                                />
                                <Area
                                  name="Gastos"
                                  type="monotone"
                                  dataKey="expense"
                                  stroke="red"
                                  fill="red"
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {Object.entries(data).map(([currency, stats]) => {
                  console.log("stats", stats);
                  const total = stats.totalIncome + stats.totalExpense;

                  stats.porcentIncome = (
                    (stats.totalIncome / total) *
                    100
                  ).toFixed(2);
                  stats.porcentExpense = (
                    (stats.totalExpense / total) *
                    100
                  ).toFixed(2);

                  return (
                    <Card>
                      <CardHeader>
                        <CardTitle>
                          Balance en Porcentaje ({currency})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {[stats] === 0 ? (
                          <Empty
                            title="Sin Balance  "
                            message="Aún no tienes transacciones."
                          ></Empty>
                        ) : (
                          <>
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart
                                width={500}
                                height={300}
                                data={[stats]}
                                margin={{
                                  top: 5,
                                  right: 30,
                                  left: 20,
                                  bottom: 5,
                                }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="Balance" />
                                <YAxis />
                                {/* <Tooltip /> */}
                                <Tooltip
                                  formatter={(value: number, name: string) => [
                                    `${value}%`,
                                    name,
                                  ]}
                                />
                                <Legend />
                                <Bar
                                  dataKey="porcentIncome"
                                  name={"Ingresos"}
                                  fill="#22c55e"
                                />
                                <Bar
                                  dataKey="porcentExpense"
                                  name={"Gastos"}
                                  fill="red"
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {Object.entries(data).map(([currency, stats]) => (
                  <Card>
                    <CardHeader>
                      <CardTitle>
                        Cuentas con Más Movimientos {currency}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {stats.trends.length === 0 ? (
                        <Empty
                          title="Sin cuenta con más movimiento"
                          message="Aún no tienes transacciones."
                        ></Empty>
                      ) : (
                        <>
                          <ResponsiveContainer width="100%" height={400}>
                            <PieChart>
                              <Pie
                                activeIndex={activeIndex}
                                activeShape={renderActiveShape}
                                data={stats.topAccounts}
                                cx="50%"
                                cy="50%"
                                innerRadius={80}
                                outerRadius={100}
                                height={400}
                                width={300}
                                fill="#22c55e"
                                dataKey="count"
                                onMouseEnter={(_, index) =>
                                  setActiveIndex(index)
                                }
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
